// Adapter layer for the signup flow: invitation-token signup with SMS verification. Reconciled to the
// authoritative BE SPEC (03_BE_SPEC_Implementation.md FEATURE_004): the OTP model is PHONE-KEYED — the
// backend stores the verified flag keyed by phone, so no sessionId/verification-token is passed between
// steps. To keep the shared PhoneVerificationPanel state machine unchanged, the adapter carries the PHONE
// through the panel's opaque `phoneChangeSessionId` / `phoneChangeToken` slots (the panel treats them as
// opaque handles; on the wire they become the {phone, otp} the BE expects).
//
// All calls expose the IS_MOCK/httpClient pattern; mocks throw ApiError so consumers can switch on
// `err.code` regardless of branch.

import { httpClient } from '@/api/http-client';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';
import {
  fetchPublishedTerm,
  fetchPublishedTermsList,
} from '@/api/adapters/terms-policies';
import type { PortalRole } from '@/shared/types/auth';
import type { SignupConsent, SignupTerm } from '@/features/auth/types/signup-terms';
import type { InvitationError, InvitationSuccess } from '@/mock/invitation.mock';

// ── DTOs ──────────────────────────────────────────────────────────────────────
// NOTE: `phoneChangeSessionId` / `phoneChangeToken` carry the PHONE in the phone-keyed model (see header).

export interface SendSignupSmsCodeResponse {
  phoneChangeSessionId: string; // = the phone (opaque handle for the shared panel)
  expiresAt: string; // ISO
}

export interface VerifySignupSmsCodeResponse {
  phoneChangeToken: string; // = the phone (opaque handle for the shared panel)
  expiresAt: string; // ISO
}

export interface CompleteSignupPayload {
  password: string;
  name: string;
  phone: string; // digits only (10–11) — the verified phone
  phoneVerificationToken?: string; // legacy slot (= phone); not sent on the wire in the phone-keyed model
  consents: SignupConsent[]; // US-SU-05: one decision per displayed term
}

export interface CompleteSignupResponse {
  userId: string;
  status: 'ACTIVE';
  role: PortalRole;
}

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

const MOCK_LATENCY_MS = 400;
const MOCK_CODE_TTL_SEC = 300;
const MOCK_RATE_LIMIT_RETRY_AFTER_SEC = 60;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const digitsOnly = (value: string): string => value.replace(/\D/g, '');
// Wire-normalize a phone for the BE: strip every non-digit so a hyphenated input (010-1234-5678) and a plain
// one (01012345678) both send the same Korean digits. The BE accepts only the Korean format (^010\d{8}$) and
// the app ships Korean-only per the customer requirement — there is no international "+" handling. Mirrors the
// BE MedicalSmsVerificationService.normalizePhone, which also strips non-digits then validates ^010\d{8}$.
const normalizePhone = (value: string): string => digitsOnly(value);

// ── US-SU-05: Signup Terms (mirrors published terms from terms-policies.ts MOCK_TERMS) ─
const MOCK_SIGNUP_TERMS: SignupTerm[] = [
  {
    termVersionId: 103,
    typeId: 1,
    name: '서비스 이용약관',
    version: 'v1.2',
    effectiveDate: '2026-04-01',
    required: true,
    content: `<h4>제1조 (목적)</h4>
<p>본 약관은 울산대학교병원이 제공하는 스마트홈케어 서비스(이하 "서비스")의 이용과 관련하여 회사와 의료진 사이의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.</p>
<h4>제2조 (용어의 정의)</h4>
<p>본 약관에서 사용하는 용어의 정의는 다음과 같습니다.</p>
<ol>
<li>"서비스"라 함은 회사가 의료진에게 제공하는 환자 관리, PRO 응답 모니터링, 위험도 평가 등 일체의 디지털 케어 서비스를 의미합니다.</li>
<li>"의료진"이라 함은 회사와 서비스 이용계약을 체결하고 회사가 제공하는 서비스를 이용하는 의사, 간호사 등 의료 전문가를 말합니다.</li>
<li>"계정"이라 함은 의료진의 식별과 서비스 이용을 위하여 회사가 부여한 이메일 주소 및 비밀번호의 조합을 의미합니다.</li>
</ol>
<h4>제3조 (약관의 효력 및 변경)</h4>
<p>회사는 본 약관의 내용을 의료진이 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다. 회사는 관련 법령을 위배하지 않는 범위에서 본 약관을 변경할 수 있으며, 약관이 변경되는 경우 적용일자 및 변경사유를 명시하여 시행일자 7일 이전부터 시행일자 전일까지 공지합니다.</p>
<h4>제4조 (서비스의 제공 및 변경)</h4>
<p>회사는 다음과 같은 업무를 수행합니다.</p>
<ol>
<li>담당 환자 등록 및 임상 정보 관리</li>
<li>환자 PRO(Patient Reported Outcome) 응답 수집 및 모니터링</li>
<li>위험도(R지수) 산출 및 등급 표시</li>
<li>중재 기록 작성 및 보관</li>
<li>기타 회사가 정하는 부가 업무</li>
</ol>
<h4>제5조 (의료진의 의무)</h4>
<p>의료진은 다음 행위를 하여서는 안 됩니다.</p>
<ol>
<li>신청 또는 변경 시 허위 내용의 등록</li>
<li>타인의 정보 도용</li>
<li>회사가 게시한 정보의 무단 변경</li>
<li>환자 개인정보의 무단 유출 또는 목적 외 이용</li>
<li>기타 관련 법령에 위반되는 행위</li>
</ol>
<h4>제6조 (서비스 이용의 제한 및 중지)</h4>
<p>회사는 의료진이 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 경고·일시정지·영구이용정지 등으로 서비스 이용을 단계적으로 제한할 수 있습니다.</p>
<p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>`,
  },
  {
    termVersionId: 202,
    typeId: 2,
    name: '개인정보 수집 및 이용 동의',
    version: 'v1.1',
    effectiveDate: '2026-03-15',
    required: true,
    content: `<h4>1. 수집하는 개인정보 항목</h4>
<p>울산대학교병원은 스마트홈케어 서비스 제공을 위하여 다음과 같은 개인정보를 수집합니다.</p>
<ul>
<li>필수 항목: 성명, 이메일 주소, 휴대전화번호, 소속 기관, 부서, 직무, 면허 정보</li>
<li>선택 항목: 프로필 사진, 비상 연락처</li>
<li>자동 수집 항목: 접속 IP, 쿠키, 서비스 이용 기록, 접속 로그, 단말기 정보(OS, 화면 해상도, 디바이스 고유 식별자)</li>
</ul>
<h4>2. 개인정보의 수집 및 이용 목적</h4>
<p>수집된 개인정보는 다음의 목적을 위해 활용됩니다.</p>
<ol>
<li>회원 식별 및 본인 확인</li>
<li>서비스 제공 및 운영, 환자 케어 의료진 연결</li>
<li>고지사항 전달, 민원처리 등 의사소통 경로 확보</li>
<li>서비스 부정이용 방지 및 보안 사고 대응</li>
<li>법령 또는 이용약관에 위반된 행위 제재 등</li>
</ol>
<h4>3. 개인정보의 보유 및 이용 기간</h4>
<p>회사는 의료진의 개인정보를 원칙적으로 회원 탈퇴 시까지 보유·이용합니다. 단, 관련 법령에 의해 보존할 필요가 있는 경우에는 해당 기간 동안 보관합니다.</p>
<ul>
<li>접속 로그: 통신비밀보호법에 따라 3개월</li>
<li>표시·광고에 관한 기록: 전자상거래법에 따라 6개월</li>
<li>계약 또는 청약철회 등에 관한 기록: 전자상거래법에 따라 5년</li>
<li>소비자 불만 또는 분쟁처리에 관한 기록: 전자상거래법에 따라 3년</li>
</ul>
<h4>4. 동의 거부 권리 및 거부 시 불이익</h4>
<p>의료진은 개인정보 수집 및 이용에 동의를 거부할 권리가 있습니다. 다만, 필수 항목 동의를 거부하는 경우 회원 가입 및 서비스 이용이 제한됩니다.</p>
<p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>`,
  },
  {
    termVersionId: 301,
    typeId: 3,
    name: '민감정보 수집 및 이용 동의',
    version: 'v1.0',
    effectiveDate: '2026-01-15',
    required: true,
    content: `<h4>1. 민감정보의 정의 및 수집 항목</h4>
<p>"민감정보"란 개인정보 보호법 제23조에 따른 사상·신념, 정치적 견해, 건강에 관한 정보 등 정보주체의 사생활을 현저히 침해할 우려가 있는 개인정보를 말합니다. 본 서비스는 다음의 민감정보를 수집합니다.</p>
<ul>
<li>정신건강 진단명 및 진단코드 (ICD-10 기반)</li>
<li>처방 약물 및 복약 이력</li>
<li>PRO 응답을 통해 수집되는 우울감, 자살 사고, 수면 패턴 등 정신건강 관련 자가 보고 데이터</li>
<li>위험도 평가 결과 및 등급</li>
<li>상담 및 중재 기록</li>
</ul>
<h4>2. 민감정보의 수집 및 이용 목적</h4>
<p>수집된 민감정보는 다음의 목적을 위해서만 활용됩니다.</p>
<ol>
<li>정신건강 사례 관리 및 위험 신호 조기 발견</li>
<li>PRO 응답 분석을 통한 임상 의사결정 지원</li>
<li>맞춤형 중재 계획 수립 및 보호자 교육 자료 제공</li>
<li>치료 효과 평가 및 임상 통계 산출 (가명처리 후 활용)</li>
</ol>
<h4>3. 민감정보의 보유 및 이용 기간</h4>
<p>수집된 민감정보는 의료법 제22조에 따른 의료 기록 보존 기간(10년)에 준하여 보관되며, 보존 기간 경과 시 지체 없이 파기합니다. 단, 정보주체의 동의가 있거나 법령에 특별한 규정이 있는 경우에는 해당 기간 동안 보관합니다.</p>
<h4>4. 민감정보의 제3자 제공</h4>
<p>회사는 원칙적으로 정보주체의 별도 동의 없이는 민감정보를 제3자에게 제공하지 않습니다. 다만, 다음의 경우에는 예외로 합니다.</p>
<ul>
<li>정보주체의 사전 동의가 있는 경우</li>
<li>법령에 특별한 규정이 있거나 법령상 의무를 준수하기 위해 불가피한 경우</li>
<li>정보주체 또는 그 법정대리인이 의사표시를 할 수 없는 상태에 있어 미리 동의를 받을 수 없는 경우로서 명백히 정보주체 또는 제3자의 급박한 생명, 신체, 재산의 이익을 위해 필요한 경우</li>
</ul>
<h4>5. 동의 거부 권리 및 거부 시 불이익</h4>
<p>의료진은 민감정보 수집 및 이용에 동의를 거부할 권리가 있습니다. 다만, 동의를 거부하는 경우 정신건강 케어 서비스의 핵심 기능 이용이 제한됩니다.</p>
<p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>`,
  },
  {
    termVersionId: 403,
    typeId: 4,
    name: '개인정보 처리방침',
    version: 'v1.3',
    effectiveDate: '2026-05-01',
    required: true,
    content: `<h4>제1조 (개인정보의 처리 목적)</h4>
<p>울산대학교병원(이하 "병원")은 개인정보 보호법 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.</p>
<h4>제2조 (개인정보의 처리 및 보유 기간)</h4>
<p>병원은 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
<h4>제3조 (정보주체와 법정대리인의 권리·의무 및 행사방법)</h4>
<p>정보주체는 병원에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
<ol>
<li>개인정보 열람 요구</li>
<li>오류 등이 있을 경우 정정 요구</li>
<li>삭제 요구</li>
<li>처리정지 요구</li>
</ol>
<h4>제4조 (개인정보의 안전성 확보 조치)</h4>
<p>병원은 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
<ul>
<li>개인정보 취급 직원의 최소화 및 교육</li>
<li>접근권한의 제한조치 및 접근통제시스템 설치</li>
<li>개인정보의 암호화</li>
<li>해킹 등에 대비한 기술적·관리적 대책</li>
<li>비인가자에 대한 출입 통제</li>
</ul>
<h4>제5조 (개인정보 보호책임자)</h4>
<p>병원은 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
<ul>
<li>개인정보 보호책임자: 울산대학교병원 의료정보부장</li>
<li>연락처: 052-250-7000</li>
</ul>
<h4>제6조 (개인정보 처리방침의 변경)</h4>
<p>이 개인정보 처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>
<p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>`,
  },
  {
    termVersionId: 501,
    typeId: 5,
    name: '마케팅 정보 수신 동의',
    version: 'v1.0',
    effectiveDate: '2026-01-01',
    required: false,
    content: `<h4>1. 마케팅 정보 수신 동의</h4>
<p>울산대학교병원은 다음과 같은 목적으로 마케팅 정보를 수집·이용하고자 합니다.</p>
<ul>
<li>신규 서비스 및 기능 안내</li>
<li>학술 세미나, 컨퍼런스, 교육 프로그램 초청</li>
<li>서비스 이용 관련 설문조사 및 피드백 요청</li>
<li>병원 뉴스레터 및 정기 소식지 발송</li>
</ul>
<h4>2. 수신 방법</h4>
<p>마케팅 정보는 이메일, SMS, 앱 푸시 알림을 통해 제공됩니다.</p>
<h4>3. 동의 철회</h4>
<p>의료진은 언제든지 마이프로필 > 알림 설정에서 마케팅 정보 수신 동의를 철회할 수 있으며, 철회 시에도 기본 서비스 이용에는 아무런 제한이 없습니다.</p>
<h4>4. 동의 거부 권리</h4>
<p>본 동의는 선택 사항이며, 동의하지 않더라도 스마트홈케어 서비스 이용에 제한이 없습니다.</p>
<p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>`,
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// Signup terms consent list (US-SU-05).
//
// The dedicated versioned endpoint `GET /api/v1/medical/auth/signup/terms?token=`
// is NOT implemented on the medical-api yet (returns 401 — no handler, not in the
// security allowlist), and `SignupRequest` has no `consents` field. So we source
// the same terms from the PUBLIC published-terms API (`GET /api/v1/medical/terms`
// (+ /{typeId})), which is live and permitAll. This keeps signup working end-to-end;
// `termVersionId` falls back to the stable `typeId` until the BE ships versioned
// signup terms + consent recording.
// TODO(BE): swap back to `endpoints.medicalStaff.signupTerms` (token-scoped, versioned)
//           and record `consents` on POST /signup once US-SU-05 lands on the BE.
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchSignupTerms(token: string): Promise<SignupTerm[]> {
  void token; // token-scoping applies only to the (future) dedicated endpoint
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    return MOCK_SIGNUP_TERMS.map((t) => ({ ...t }));
  }
  // Public terms carry no version id; the list endpoint also strips `content`, so
  // fetch each term's detail to populate the [보기] modal body.
  const summaries = await fetchPublishedTermsList();
  const full = await Promise.all(
    summaries.map((s) => fetchPublishedTerm(s.typeId)),
  );
  return full.map((term) => ({
    termVersionId: term.typeId,
    typeId: term.typeId,
    name: term.name,
    version: term.version,
    effectiveDate: term.effectiveDate,
    required: term.required,
    content: term.content,
  }));
}

// Mock-only state: phone → code-expiry. Real BE owns the canonical (phone-keyed) state.
const mockVerified = new Set<string>();
const mockCodeExpiry = new Map<string, number>();

// Deterministic phone → scenario map (dev/QA). Error codes reconciled to the SPEC.
const MOCK_PHONE_SCENARIOS: Record<string, () => never> = {
  '01000000000': () => {
    throw new ApiError('PHONE_INVALID', '올바른 휴대폰 번호 형식을 입력해주세요. (숫자만 10~11자리)', {
      phone: 'invalid',
    });
  },
  '01011111111': () => {
    throw new ApiError('SMS_SEND_FAILED', '인증번호 발송에 실패했습니다.');
  },
  '01022222222': () => {
    throw new ApiError('SIGNUP_SMS_RATE_LIMIT', '잠시 후 다시 시도해주세요.', undefined, {
      retryAfterSeconds: MOCK_RATE_LIMIT_RETRY_AFTER_SEC,
    });
  },
  '01033333333': () => {
    throw new ApiError('PHONE_DUPLICATE', '입력하신 정보로 가입할 수 없습니다. 다시 확인해 주세요.', {
      phone: 'registered',
    });
  },
  '01099999999': () => {
    throw new ApiError('INTERNAL_ERROR', 'Simulated server error');
  },
};

// Deterministic verification-code scenarios (dev/QA). Error codes reconciled to the SPEC.
const MOCK_CODE_SCENARIOS: Record<string, () => never> = {
  '000000': () => {
    throw new ApiError('OTP_INVALID', '인증번호가 일치하지 않습니다.', { code: 'mismatch' });
  },
  '999999': () => {
    throw new ApiError('OTP_EXPIRED', '인증번호가 만료되었습니다. 다시 인증번호를 받아주세요.', { code: 'expired' });
  },
  '500000': () => {
    throw new ApiError('INTERNAL_ERROR', 'Simulated server error');
  },
};

const MOCK_VERIFICATION_CODE = '123456';

// Deterministic invitation-token → completeSignup error scenarios. Reconciled to the SPEC.
const MOCK_SIGNUP_TOKEN_SCENARIOS: Record<string, () => never> = {
  'expired-invitation': () => {
    throw new ApiError('SIGNUP_TOKEN_EXPIRED', '초대 링크가 만료되었습니다. 관리자에게 재초대를 요청해주세요.');
  },
  'registered-invitation': () => {
    throw new ApiError('SIGNUP_ALREADY_REGISTERED', '이미 가입된 계정입니다. 로그인 화면을 이용해주세요.');
  },
  'unverified-mobile': () => {
    throw new ApiError('SIGNUP_PHONE_UNVERIFIED', '휴대폰 인증을 완료해주세요.');
  },
  'server-error': () => {
    throw new ApiError('INTERNAL_ERROR', 'Simulated server error');
  },
  'no-response': () => {
    throw new ApiError('INTERNAL_ERROR', 'Simulated network timeout — no server response');
  },
};

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/auth/signup/phone-verify/request  body {phone}  → {expiresAt}
// ──────────────────────────────────────────────────────────────────────────────
export async function sendSignupSmsCode(
  phone: string,
  invitationToken?: string, // sent so the BE can refuse a phone that differs from the invite-prefilled one
): Promise<SendSignupSmsCodeResponse> {
  const digits = normalizePhone(phone);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    // Korean numbers must be 10–11 digits (the BE owns the authoritative ^010\d{8}$ validation).
    if (digits.length < 10 || digits.length > 11) {
      throw new ApiError('PHONE_INVALID', '올바른 휴대폰 번호 형식을 입력해주세요. (숫자만 10~11자리)', {
        phone: 'invalid',
      });
    }
    const scenario = MOCK_PHONE_SCENARIOS[digits];
    if (scenario) scenario();
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    mockCodeExpiry.set(digits, Date.parse(expiresAt));
    return { phoneChangeSessionId: digits, expiresAt };
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<{ expiresAt: string }>>(
      endpoints.medicalStaff.signupSendCode,
      { phone: digits, invitationToken },
    );
    return { phoneChangeSessionId: digits, expiresAt: data.data.expiresAt };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/auth/signup/phone-verify/confirm  body {phone, otp}  → {phoneVerified}
// `phone` is the opaque handle the panel passes back as `phoneChangeSessionId`.
// ──────────────────────────────────────────────────────────────────────────────
export async function verifySignupSmsCode(
  phone: string,
  code: string,
): Promise<VerifySignupSmsCodeResponse> {
  const digits = normalizePhone(phone);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const scenario = MOCK_CODE_SCENARIOS[code];
    if (scenario) scenario();
    const expiry = mockCodeExpiry.get(digits);
    if (expiry && Date.now() > expiry) {
      throw new ApiError('OTP_EXPIRED', '인증번호가 만료되었습니다. 다시 인증번호를 받아주세요.', {
        code: 'expired',
      });
    }
    if (code !== MOCK_VERIFICATION_CODE) {
      throw new ApiError('OTP_INVALID', '인증번호가 일치하지 않습니다.', { code: 'mismatch' });
    }
    mockVerified.add(digits);
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    return { phoneChangeToken: digits, expiresAt };
  }
  try {
    await httpClient.post<ApiSuccess<{ phoneVerified: boolean }>>(
      endpoints.medicalStaff.signupVerifyCode,
      { phone: digits, otp: code },
    );
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    return { phoneChangeToken: digits, expiresAt };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/auth/signup  body {invitationToken, name, password, phone}  → 201 {staffId,email,role}
// ──────────────────────────────────────────────────────────────────────────────
export async function completeSignup(
  invitationToken: string,
  payload: CompleteSignupPayload,
): Promise<CompleteSignupResponse> {
  const phone = normalizePhone(payload.phone);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const scenario = MOCK_SIGNUP_TOKEN_SCENARIOS[invitationToken];
    if (scenario) scenario();
    if (!mockVerified.has(phone)) {
      throw new ApiError('SIGNUP_PHONE_UNVERIFIED', '휴대폰 인증을 완료해주세요.');
    }
    // US-SU-05: validate all required terms are agreed (mirrors Admin Web mock)
    const agreedVersionIds = new Set(payload.consents?.filter((c) => c.agreed).map((c) => c.termVersionId) ?? []);
    const allRequiredAgreed = MOCK_SIGNUP_TERMS.every(
      (term) => !term.required || agreedVersionIds.has(term.termVersionId),
    );
    if (!allRequiredAgreed) {
      throw new ApiError('REQUIRED_TERMS_NOT_AGREED', '필수 약관에 모두 동의해주세요.');
    }
    mockVerified.delete(phone);
    const role: PortalRole = invitationToken.includes('nurse') ? 'NURSE' : 'DOCTOR';
    return { userId: `mock-user-${Date.now()}`, status: 'ACTIVE', role };
  }
  try {
    // Phone-keyed model: the verified flag lives on the BE keyed by phone; no verification token in the body.
    const { data } = await httpClient.post<ApiSuccess<{ staffId: number; email: string; role: PortalRole }>>(
      endpoints.medicalStaff.signup,
      { invitationToken, name: payload.name, password: payload.password, phone, consents: payload.consents },
    );
    return { userId: String(data.data.staffId), status: 'ACTIVE', role: data.data.role };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// GET /api/v1/medical/auth/invitation/:token — validate the invite + prefill the form
// ──────────────────────────────────────────────────────────────────────────────
interface InvitationApiResponse {
  email: string;
  institution: string;
  department: string;
  role: PortalRole;
  expiresAt?: string;
}

/**
 * Real invitation lookup (SPEC FEATURE_004). Maps the BE success/error envelope to the same
 * {@link InvitationSuccess}/{@link InvitationError} discriminated union the mock returns, so
 * `useInvitationValidation` is branch-agnostic. The BE returns PortalRole directly (DOCTOR/NURSE/CRC).
 */
export async function fetchInvitation(token: string): Promise<InvitationSuccess | InvitationError> {
  try {
    const { data } = await httpClient.get<ApiSuccess<InvitationApiResponse>>(
      endpoints.medicalStaff.invitation(token),
    );
    const inv = data.data;
    return {
      valid: true,
      email: inv.email,
      institution: inv.institution,
      department: inv.department,
      role: inv.role as PortalRole,
    };
  } catch (err) {
    const mapped = mapAxiosError(err);
    const reason: InvitationError['reason'] =
      mapped.code === 'SIGNUP_TOKEN_EXPIRED'
        ? 'expired'
        : mapped.code === 'SIGNUP_ALREADY_REGISTERED'
          ? 'already-registered'
          : mapped.code === 'SIGNUP_TOKEN_NOT_FOUND'
            ? 'invalid'
            : 'server-error';
    return { valid: false, reason };
  }
}
